import { useState, useEffect, useCallback } from 'react';
import { PenLine, Check, X, Copy, Eye, Trash2, Loader2, Upload, Star, StarOff, Palette } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { API } from './constants';

const COULEURS_SIGNATURE = [
  { label: 'Or Le Mas', value: '#C9A84C' },
  { label: 'Bleu nuit', value: '#1B2A52' },
  { label: 'Teal', value: '#009893' },
  { label: 'Bordeaux', value: '#8B2252' },
  { label: 'Gris', value: '#475569' },
  { label: 'Vert', value: '#059669' },
  { label: 'Rouge', value: '#DC2626' },
  { label: 'Violet', value: '#7C3AED' },
];

const SOCIAL_NETWORKS = [
  { key: 'linkedin', label: 'LinkedIn', icon: 'https://cdn-icons-png.flaticon.com/24/3536/3536505.png' },
  { key: 'instagram', label: 'Instagram', icon: 'https://cdn-icons-png.flaticon.com/24/2111/2111463.png' },
  { key: 'facebook', label: 'Facebook', icon: 'https://cdn-icons-png.flaticon.com/24/733/733547.png' },
  { key: 'tripadvisor', label: 'TripAdvisor', icon: 'https://cdn-icons-png.flaticon.com/24/3128/3128279.png' },
  { key: 'twitter', label: 'X', icon: 'https://cdn-icons-png.flaticon.com/24/5968/5968830.png' },
  { key: 'tiktok', label: 'TikTok', icon: 'https://cdn-icons-png.flaticon.com/24/3046/3046121.png' },
];

const TEMPLATES = [
  {
    id: 'restaurant',
    label: 'Restaurant Le Mas',
    desc: 'Signature restaurant classique',
    data: {
      nom: 'Laurent San Martin', poste: 'Gerant', tel: '06 87 88 59 25',
      email: 'contact@restaurant-le-mas.fr', site: 'www.restaurant-le-mas.fr',
      adresse: '6 Av. Marechal Joffre, 66250 Saint-Laurent-de-la-Salanque',
      entreprise: 'Le Mas', sous_titre: 'Restaurant', slogan: 'Cuisine catalane & produits locaux',
      couleur: '#C9A84C', logo_url: '', socials: { facebook: 'https://www.facebook.com/lemasrestaurant', instagram: 'https://www.instagram.com/lemasrestaurant/' },
    },
  },
  {
    id: 'traiteur',
    label: 'Le Traiteur du Mas',
    desc: 'Signature traiteur commerciale',
    data: {
      nom: 'Laurent San Martin', poste: 'Traiteur', tel: '06 87 88 59 25',
      email: 'contact@traiteur-du-mas.fr', site: 'www.restaurant-le-mas.fr',
      adresse: '6 Av. Marechal Joffre, 66250 Saint-Laurent-de-la-Salanque',
      entreprise: 'Le Traiteur du Mas', sous_titre: 'Traiteur & Evenementiel', slogan: 'Art de recevoir en pays catalan',
      couleur: '#009893', logo_url: '', socials: { facebook: 'https://www.facebook.com/lemasrestaurant', instagram: 'https://www.instagram.com/lemasrestaurant/' },
    },
  },
  {
    id: 'minimaliste',
    label: 'Minimaliste',
    desc: 'Signature sobre et elegante',
    data: {
      nom: 'Laurent San Martin', poste: '', tel: '06 87 88 59 25',
      email: 'contact@restaurant-le-mas.fr', site: 'www.restaurant-le-mas.fr',
      adresse: 'Saint-Laurent-de-la-Salanque', entreprise: 'Le Mas',
      sous_titre: '', slogan: '', couleur: '#1B2A52', logo_url: '', socials: {},
    },
  },
];

export function CrmSignatures() {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    nom: '', poste: '', tel: '', email: '', site: 'www.restaurant-le-mas.fr',
    adresse: 'Saint-Laurent-de-la-Salanque', entreprise: 'Le Mas',
    sous_titre: 'Restaurant & Traiteur', slogan: '', couleur: '#C9A84C', logo_url: '',
    socials: {},
  });
  const [editing, setEditing] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/crm/signatures`);
      setSignatures(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const buildSignatureHtml = (sig) => {
    const c = sig.couleur || '#C9A84C';
    const socials = sig.socials || {};
    const socialIcons = {
      linkedin: 'https://cdn-icons-png.flaticon.com/24/3536/3536505.png',
      instagram: 'https://cdn-icons-png.flaticon.com/24/2111/2111463.png',
      facebook: 'https://cdn-icons-png.flaticon.com/24/733/733547.png',
      tripadvisor: 'https://cdn-icons-png.flaticon.com/24/3128/3128279.png',
      twitter: 'https://cdn-icons-png.flaticon.com/24/5968/5968830.png',
      tiktok: 'https://cdn-icons-png.flaticon.com/24/3046/3046121.png',
    };
    const socialLinks = Object.entries(socials)
      .filter(([, url]) => url)
      .map(([key, url]) => `<a href="${url}" target="_blank" style="text-decoration:none;margin-right:8px;"><img src="${socialIcons[key] || ''}" alt="${key}" width="20" height="20" style="vertical-align:middle;border:0;" /></a>`)
      .join('');
    const socialRow = socialLinks ? `<tr><td colspan="3" style="padding-top:8px;">${socialLinks}</td></tr>` : '';
    const logoTd = sig.logo_url ? `<td style="padding-right:14px;vertical-align:top;"><img src="${sig.logo_url}" alt="${sig.entreprise || 'Logo'}" width="60" height="60" style="border-radius:8px;border:0;" /></td>` : '';

    return `<table style="font-family:Arial,sans-serif;font-size:13px;color:#333;border-collapse:collapse;">
  <tr>
    ${logoTd}
    <td style="border-right:3px solid ${c};padding-right:16px;">
      <strong style="font-size:15px;color:#1B2A52;">${sig.nom || ''}</strong><br>
      <span style="color:${c};font-size:11px;text-transform:uppercase;letter-spacing:1px;">${sig.entreprise || 'Le Mas'}</span><br>
      <span style="font-size:11px;color:#666;">${sig.poste || ''}</span>
    </td>
    <td style="padding-left:16px;">
      ${sig.tel ? `<span style="font-size:12px;">Tel. ${sig.tel}</span><br>` : ''}
      ${sig.email ? `<a href="mailto:${sig.email}" style="font-size:12px;color:#333;text-decoration:none;">${sig.email}</a><br>` : ''}
      ${sig.site ? `<a href="https://${sig.site}" style="font-size:12px;color:${c};text-decoration:none;">${sig.site}</a><br>` : ''}
      <span style="font-size:11px;color:#999;">${sig.adresse || ''}</span>
    </td>
  </tr>
  ${socialRow}
  ${sig.slogan ? `<tr><td colspan="3" style="padding-top:8px;border-top:1px solid #eee;"><em style="font-size:11px;color:#999;">${sig.slogan}</em></td></tr>` : ''}
</table>`;
  };

  const copySignature = async (html) => {
    try {
      const blob = new Blob([html], { type: 'text/html' });
      const plainBlob = new Blob([html], { type: 'text/plain' });
      const item = new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob });
      await navigator.clipboard.write([item]);
      toast.success('Signature copiee !');
    } catch {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      tmp.style.position = 'fixed';
      tmp.style.left = '-9999px';
      document.body.appendChild(tmp);
      const range = document.createRange();
      range.selectNodeContents(tmp);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand('copy');
      sel.removeAllRanges();
      document.body.removeChild(tmp);
      toast.success('Signature copiee !');
    }
  };

  const saveSig = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `${API}/api/crm/signatures/${editing}` : `${API}/api/crm/signatures`;
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) {
        toast.success(editing ? 'Signature mise a jour' : 'Signature creee');
        load();
        resetForm();
      }
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const deleteSig = async (id) => {
    if (!window.confirm('Supprimer cette signature ?')) return;
    try {
      await fetch(`${API}/api/crm/signatures/${id}`, { method: 'DELETE' });
      toast.success('Supprimee');
      load();
    } catch { toast.error('Erreur'); }
  };

  const setActive = async (id) => {
    try {
      const res = await fetch(`${API}/api/crm/signatures/${id}/set-active`, { method: 'POST' });
      if (res.ok) {
        toast.success('Signature activee pour tous les envois !');
        load();
      }
    } catch { toast.error('Erreur'); }
  };

  const editSig = (sig) => {
    setEditing(sig.id);
    setForm({
      nom: sig.nom || '', poste: sig.poste || '', tel: sig.tel || '', email: sig.email || '',
      site: sig.site || '', adresse: sig.adresse || '', entreprise: sig.entreprise || 'Le Mas',
      sous_titre: sig.sous_titre || '', slogan: sig.slogan || '', couleur: sig.couleur || '#C9A84C',
      logo_url: sig.logo_url || '', socials: sig.socials || {},
    });
    setShowTemplates(false);
  };

  const applyTemplate = (t) => {
    setForm({ ...t.data });
    setEditing(null);
    setShowTemplates(false);
    setShowPreview(true);
    toast.success(`Template "${t.label}" applique`);
  };

  const resetForm = () => {
    setEditing(null);
    setForm({
      nom: '', poste: '', tel: '', email: '', site: 'www.restaurant-le-mas.fr',
      adresse: 'Saint-Laurent-de-la-Salanque', entreprise: 'Le Mas',
      sous_titre: 'Restaurant & Traiteur', slogan: '', couleur: '#C9A84C', logo_url: '',
      socials: {},
    });
    setShowPreview(false);
  };

  const activeSignature = signatures.find(s => s.is_active);

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  return (
    <div className="space-y-4" data-testid="crm-signatures">
      {/* Active signature banner */}
      {activeSignature && (
        <div className="p-3 rounded-xl bg-[rgb(var(--lm-gold)/0.08)] border border-[rgb(var(--lm-gold)/0.2)]" data-testid="active-signature-banner">
          <div className="flex items-center gap-2 mb-2">
            <Star size={14} className="text-[rgb(var(--lm-gold))] fill-[rgb(var(--lm-gold))]" />
            <span className="text-[10px] uppercase tracking-wider font-medium text-[rgb(var(--lm-gold))]">Signature active sur tous les envois</span>
          </div>
          <div className="bg-white/80 rounded-lg p-3 border border-[rgb(var(--lm-border))]/20" dangerouslySetInnerHTML={{ __html: buildSignatureHtml(activeSignature) }} />
        </div>
      )}

      {/* Signature par module - RIGHT AFTER active banner */}
      <SignatureAssignments signatures={signatures} />

      {/* Templates */}
      <div className="bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30 p-4">
        <button onClick={() => setShowTemplates(!showTemplates)}
          className="flex items-center gap-2 text-xs font-medium text-[rgb(var(--lm-text))] mb-2" data-testid="toggle-templates">
          <Palette size={14} className="text-[rgb(var(--lm-gold))]" />
          Templates de signature
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">({TEMPLATES.length})</span>
        </button>
        {showTemplates && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 animate-slide-up">
            {TEMPLATES.map(t => (
              <button key={t.id} onClick={() => applyTemplate(t)}
                className="text-left p-3 rounded-lg border border-[rgb(var(--lm-border))]/30 hover:border-[rgb(var(--lm-gold))]/50 transition-all group"
                data-testid={`template-${t.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.data.couleur }} />
                  <span className="text-[11px] font-medium text-[rgb(var(--lm-text))]">{t.label}</span>
                </div>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{t.desc}</p>
                <div className="mt-2 p-2 bg-[rgb(var(--lm-surface))] rounded border border-[rgb(var(--lm-border))]/20 scale-[0.7] origin-top-left" style={{ width: '142%' }}
                  dangerouslySetInnerHTML={{ __html: buildSignatureHtml(t.data) }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Form */}
      <div className="bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30 p-4 space-y-3">
        <h3 className="text-xs font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider flex items-center gap-2">
          <PenLine size={14} /> {editing ? 'Modifier la signature' : 'Nouvelle signature'}
        </h3>

        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Nom *</label>
            <Input value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))} placeholder="Laurent San Martin" className="text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="sig-nom" /></div>
          <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Poste</label>
            <Input value={form.poste} onChange={e => setForm(p => ({ ...p, poste: e.target.value }))} placeholder="Gerant" className="text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" /></div>
          <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Telephone</label>
            <Input value={form.tel} onChange={e => setForm(p => ({ ...p, tel: e.target.value }))} placeholder="06 87 88 59 25" className="text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" /></div>
          <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Email</label>
            <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="contact@..." className="text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" /></div>
          <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Site web</label>
            <Input value={form.site} onChange={e => setForm(p => ({ ...p, site: e.target.value }))} className="text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" /></div>
          <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Adresse</label>
            <Input value={form.adresse} onChange={e => setForm(p => ({ ...p, adresse: e.target.value }))} className="text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" /></div>
          <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Entreprise</label>
            <Input value={form.entreprise} onChange={e => setForm(p => ({ ...p, entreprise: e.target.value }))} className="text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" /></div>
          <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Slogan</label>
            <Input value={form.slogan} onChange={e => setForm(p => ({ ...p, slogan: e.target.value }))} placeholder="Cuisine catalane..." className="text-xs h-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" /></div>
        </div>

        <div><label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-0.5">Logo / Photo</label>
          <div className="flex gap-2 items-end">
            <div className="flex-1 relative">
              <Input value={form.logo_url} onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))} placeholder="URL ou uploadez..." className="text-xs h-8 pr-8 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]" data-testid="sig-logo-url" />
              {form.logo_url && (
                <button onClick={() => setForm(p => ({ ...p, logo_url: '' }))} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))] hover:text-red-400"><X size={12} /></button>
              )}
            </div>
            <label className="cursor-pointer px-3 h-8 rounded-lg bg-[rgb(var(--lm-gold)/0.1)] hover:bg-[rgb(var(--lm-gold)/0.2)] border border-[rgb(var(--lm-gold)/0.3)] text-[rgb(var(--lm-gold))] text-[10px] font-medium flex items-center gap-1 transition-all" data-testid="sig-upload-btn">
              <Upload size={12} /> Photo
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 2 * 1024 * 1024) { toast.error('Image trop grande (max 2MB)'); return; }
                const fd = new FormData();
                fd.append('file', file);
                try {
                  const res = await fetch(`${API}/api/crm/upload-image`, { method: 'POST', body: fd });
                  const data = await res.json();
                  if (res.ok) { setForm(p => ({ ...p, logo_url: data.url })); toast.success('Logo uploade !'); }
                  else { toast.error(data.detail || 'Erreur upload'); }
                } catch { toast.error('Erreur upload'); }
              }} />
            </label>
            {form.logo_url && <img src={form.logo_url} alt="Logo" className="w-8 h-8 rounded-lg border border-[rgb(var(--lm-border))] object-cover" />}
          </div>
        </div>

        {/* Social media */}
        <div>
          <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1.5">Reseaux sociaux</label>
          <div className="space-y-1.5">
            {SOCIAL_NETWORKS.map(s => {
              const active = !!(form.socials && form.socials[s.key]);
              return (
                <div key={s.key} className="flex items-center gap-2">
                  <button
                    onClick={() => setForm(p => ({ ...p, socials: { ...p.socials, [s.key]: active ? '' : (p.socials?.[s.key] || '') } }))}
                    className={`w-7 h-7 rounded-lg border flex items-center justify-center transition-all ${active ? 'border-[rgb(var(--lm-gold))] bg-[rgb(var(--lm-gold)/0.1)]' : 'border-[rgb(var(--lm-border))] bg-[rgb(var(--lm-surface))] opacity-40'}`}
                    data-testid={`social-toggle-${s.key}`}>
                    <img src={s.icon} alt={s.label} className="w-4 h-4" />
                  </button>
                  <Input
                    value={form.socials?.[s.key] || ''}
                    onChange={e => setForm(p => ({ ...p, socials: { ...p.socials, [s.key]: e.target.value } }))}
                    placeholder={`URL ${s.label}`}
                    className={`text-xs h-7 flex-1 bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] ${!active && !(form.socials?.[s.key]) ? 'opacity-40' : ''}`}
                    data-testid={`social-url-${s.key}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Color picker */}
        <div>
          <label className="text-[10px] text-[rgb(var(--lm-text-muted))] block mb-1">Couleur accent</label>
          <div className="flex gap-1.5 flex-wrap">
            {COULEURS_SIGNATURE.map(c => (
              <button key={c.value} onClick={() => setForm(p => ({ ...p, couleur: c.value }))}
                className={`w-7 h-7 rounded-full border-2 transition-all ${form.couleur === c.value ? 'border-[rgb(var(--lm-text))] scale-110' : 'border-transparent hover:scale-105'}`}
                style={{ backgroundColor: c.value }} title={c.label}
                data-testid={`color-${c.value}`} />
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={() => setShowPreview(!showPreview)}
            className="flex-1 py-2 rounded-lg bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-secondary))] text-xs font-medium hover:bg-[rgb(var(--lm-border))] flex items-center justify-center gap-1 border border-[rgb(var(--lm-border))]/30"
            data-testid="sig-preview-btn">
            <Eye size={12} /> {showPreview ? 'Masquer' : 'Apercu'}
          </button>
          <button onClick={saveSig} disabled={saving || !form.nom.trim()}
            className="flex-1 py-2 rounded-lg bg-[rgb(var(--lm-gold))] text-white text-xs font-medium hover:opacity-90 flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid="sig-save-btn">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {editing ? 'Mettre a jour' : 'Sauvegarder'}
          </button>
          {editing && (
            <button onClick={resetForm} className="px-3 py-2 rounded-lg bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))] text-xs hover:bg-[rgb(var(--lm-border))] border border-[rgb(var(--lm-border))]/30">
              Annuler
            </button>
          )}
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30 p-4 space-y-3 animate-slide-up" data-testid="sig-preview">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Apercu</h3>
            <button onClick={() => copySignature(buildSignatureHtml(form))}
              className="px-3 py-1 bg-[rgb(var(--lm-gold))] text-white text-xs rounded-lg flex items-center gap-1 hover:opacity-90" data-testid="sig-copy-preview">
              <Copy size={10} /> Copier
            </button>
          </div>
          <div className="border border-[rgb(var(--lm-border))]/20 rounded-lg p-4 bg-white" dangerouslySetInnerHTML={{ __html: buildSignatureHtml(form) }} />
        </div>
      )}

      {/* Saved signatures list */}
      {signatures.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">
            Signatures sauvegardees ({signatures.length})
          </h3>
          {signatures.map(sig => {
            const isActive = sig.is_active;
            const activeSocials = SOCIAL_NETWORKS.filter(s => sig.socials && sig.socials[s.key]);
            return (
              <div key={sig.id} className={`bg-[rgb(var(--lm-card))] rounded-xl border p-3 transition-all ${isActive ? 'border-[rgb(var(--lm-gold))] shadow-[0_0_0_1px_rgb(var(--lm-gold)/0.3)]' : 'border-[rgb(var(--lm-border))]/30 hover:border-[rgb(var(--lm-gold))]/30'}`} data-testid={`sig-${sig.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {isActive && <Star size={12} className="text-[rgb(var(--lm-gold))] fill-[rgb(var(--lm-gold))]" />}
                    <span className="text-sm font-medium text-[rgb(var(--lm-text))]">{sig.nom}</span>
                    <span className="text-xs text-[rgb(var(--lm-text-muted))]">{sig.entreprise}</span>
                    {isActive && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))] font-medium uppercase tracking-wider">Active</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    {activeSocials.map(s => (
                      <img key={s.key} src={s.icon} alt={s.label} className="w-3.5 h-3.5 opacity-60" />
                    ))}
                    <div className="w-3 h-3 rounded-full ml-1" style={{ backgroundColor: sig.couleur || '#C9A84C' }} />
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {!isActive && (
                    <button onClick={() => setActive(sig.id)}
                      className="px-3 py-1 rounded-lg bg-[rgb(var(--lm-gold)/0.1)] text-[rgb(var(--lm-gold))] text-[10px] font-medium hover:bg-[rgb(var(--lm-gold)/0.2)] flex items-center gap-1 border border-[rgb(var(--lm-gold)/0.2)]" data-testid={`activate-sig-${sig.id}`}>
                      <Star size={10} /> Activer
                    </button>
                  )}
                  <button onClick={() => editSig(sig)}
                    className="px-3 py-1 rounded-lg bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-secondary))] text-[10px] font-medium hover:bg-[rgb(var(--lm-border))] flex items-center gap-1 border border-[rgb(var(--lm-border))]/30" data-testid={`edit-sig-${sig.id}`}>
                    <PenLine size={10} /> Modifier
                  </button>
                  <button onClick={() => copySignature(buildSignatureHtml(sig))}
                    className="px-3 py-1 rounded-lg bg-[rgb(var(--lm-gold))] text-white text-[10px] font-medium hover:opacity-90 flex items-center gap-1" data-testid={`copy-sig-${sig.id}`}>
                    <Copy size={10} /> Copier
                  </button>
                  <button onClick={() => deleteSig(sig.id)}
                    className="px-2 py-1 rounded-lg bg-red-500/10 text-red-400 text-[10px] hover:bg-red-500/20 border border-red-500/20" data-testid={`del-sig-${sig.id}`}>
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info banner */}
      <div className="p-3 rounded-xl bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))]/20">
        <p className="text-[10px] text-[rgb(var(--lm-text-muted))] leading-relaxed">
          La signature <strong>active</strong> est utilisee par defaut. Vous pouvez assigner une signature specifique a chaque module ci-dessus.
        </p>
      </div>
    </div>
  );
}


function SignatureAssignments({ signatures }) {
  const [assignments, setAssignments] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/crm/signature-assignments`);
      const data = await res.json();
      setModules(data.modules || []);
      setAssignments(data.assignments || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const assign = async (moduleId, sigId) => {
    try {
      await fetch(`${API}/api/crm/signature-assignments/${moduleId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_id: sigId })
      });
      setAssignments(prev => {
        const filtered = prev.filter(a => a.module !== moduleId);
        return [...filtered, { module: moduleId, signature_id: sigId }];
      });
      toast.success('Signature assignee');
    } catch { toast.error('Erreur'); }
  };

  const getAssignment = (moduleId) => {
    const a = assignments.find(a => a.module === moduleId);
    return a?.signature_id || '';
  };

  const activeSig = signatures.find(s => s.is_active);

  if (loading) return null;

  return (
    <div className="space-y-3" data-testid="signature-assignments">
      <h3 className="text-xs font-bold text-[rgb(var(--lm-text))] uppercase tracking-wider">Signature par module</h3>
      <div className="space-y-2">
        {modules.map(mod => {
          const currentSigId = getAssignment(mod.id);
          const currentSig = signatures.find(s => s.id === currentSigId);
          return (
            <div key={mod.id} className="flex items-center gap-3 p-3 rounded-xl bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/20" data-testid={`assign-${mod.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[rgb(var(--lm-text))]">{mod.label}</p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">
                  {currentSig ? currentSig.entreprise || currentSig.nom : `Par defaut${activeSig ? ` (${activeSig.entreprise || activeSig.nom})` : ''}`}
                </p>
              </div>
              <select
                value={currentSigId}
                onChange={e => assign(mod.id, e.target.value)}
                className="text-xs h-8 px-2 rounded-lg border border-[rgb(var(--lm-border))]/30 bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text))] min-w-[160px]"
              >
                <option value="">Par defaut (active)</option>
                {signatures.map(s => (
                  <option key={s.id} value={s.id}>{s.entreprise || s.nom}{s.is_active ? ' (active)' : ''}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
